import { LazoView, useLazoView } from "@/contexts/CurrentLazoViewContext";
import { Colors } from "@/enums/Colors";
import { LazoPaths } from "@/enums/LazoPaths";
import User from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/core/User";
import AuthService from "@/external/ensolvers-core-frontend-mui-v5/submodules/fox-typescript/services/AuthService";
import { useResponsive } from "@/hooks/useResponsive";
import { LazoRoutes, lazoRoutesSpecification } from "@/types/BaseProjectRoutes";
import { UserRole } from "@/types/UserRole";
import { AdminPanelSettings, Settings } from "@mui/icons-material";
import { Box, Button, Divider, Tooltip, styled } from "@mui/material";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";

const SectionContainer = styled(Box)({
  display: "flex",
  width: "100%",
  alignItems: "flex-start",
  flexDirection: "column",
  paddingLeft: "1.9rem",
  gap: ".5rem",
});

const StyledButton = styled(Button)(({ theme }) => ({
  width: "100%",
  justifyContent: "flex-start",
  textAlign: "left", // Ensure text is left-aligned
  color: Colors.LIGHT_GRAY_BFC2C6,
  padding: "0.3rem 0.5rem",
  fontWeight: 400,
  borderRadius: "0.500rem",
  fontSize: "0.75rem", // Smaller font since this section is less important
  textTransform: "none",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.625rem", // Smaller on mobile
  },
  "& span:first-of-type": {
    filter: "brightness(8)",
  },
  "& .MuiButton-startIcon": {
    marginRight: "8px", // Ensure consistent spacing between icon and text
  },
}));

const StyledNavLink = styled(NavLink)({
  width: "10.750rem",
  borderRadius: "0.5rem",
  marginLeft: "-0.3rem",
  "&:hover": {
    backgroundColor: Colors.DARK_GREY_3E3E3E,
  },
});

interface DrawerSettingsSupportSectionProps {
  open: boolean;
}

export function DrawerSettingsSupportSection({ open }: DrawerSettingsSupportSectionProps) {
  const { t } = useTranslation("navigation");
  const { lazoView } = useLazoView();
  const { isDesktop } = useResponsive();
  const loggedUser: User = AuthService.getUser();
  const isAppAdmin = loggedUser.userRoles.some((r) => r.name === UserRole.SUPERADMIN);

  const location = useLocation();

  const isSettingsActive = location.pathname.includes(lazoView === LazoView.LAZO_ONE ? LazoPaths.ONE_SETTINGS : LazoPaths.PORTAL_SETTINGS);
  const isAdminToolsActive = location.pathname.includes(lazoRoutesSpecification[LazoRoutes.ADMIN_TOOLS].path);

  return (
    <SectionContainer sx={{ paddingLeft: open ? "1.9rem" : "0", alignItems: open ? "flex-start" : "center" }}>
      {!isDesktop && (
        <StyledNavLink
          to={lazoView === LazoView.LAZO_ONE ? LazoPaths.ONE_SETTINGS : LazoPaths.PORTAL_SETTINGS}
          sx={{ width: open ? "10.750rem" : "auto", marginLeft: open ? "-0.3rem" : "0" }}
        >
          <Tooltip title={!open ? t("footer.settings") : ""} arrow placement="right">
            <StyledButton
              startIcon={<Settings sx={{ color: isSettingsActive ? Colors.LAZO_PURPLE : Colors.LIGHT_GRAY_BFC2C6 }} />}
              sx={{
                justifyContent: open ? "flex-start" : "center",
                "& .MuiButton-startIcon": { margin: open ? undefined : 0 },
              }}
            >
              {open && t("footer.settings")}
            </StyledButton>
          </Tooltip>
        </StyledNavLink>
      )}
      {isAppAdmin && <Divider sx={{ width: "100%", borderColor: Colors.LIGHT_GRAY_4a4b4e }} />}
      {isAppAdmin && (
        <StyledNavLink
          to={lazoRoutesSpecification[LazoRoutes.ADMIN_TOOLS].path}
          sx={{ width: open ? "10.750rem" : "auto", marginLeft: open ? "-0.3rem" : "0" }}
        >
          <Tooltip title={!open ? t("footer.adminTools") : ""} arrow placement="right">
            <StyledButton
              startIcon={<AdminPanelSettings sx={{ color: isAdminToolsActive ? Colors.LAZO_PURPLE : Colors.LIGHT_GRAY_BFC2C6 }} />}
              sx={{
                justifyContent: open ? "flex-start" : "center",
                "& .MuiButton-startIcon": { margin: open ? undefined : 0 },
              }}
            >
              {open && t("footer.adminTools")}
            </StyledButton>
          </Tooltip>
        </StyledNavLink>
      )}
    </SectionContainer>
  );
}
